import React, { useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { useNavigate } from 'react-router-dom';
import { MdPerson, MdSettings, MdCheckCircle, MdCancel, MdScience } from 'react-icons/md';
import './Admin.css';

const API_URL = 'https://j1jjasmb97.execute-api.eu-north-1.amazonaws.com/LoadLogsAndConfigurations-API';

function Admin() {
  const { userData, jwtToken, isAdmin, setViewingAs } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchUsers();
  }, [isAdmin, jwtToken]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${jwtToken}`
        },
        body: JSON.stringify({
          list_users: true
        })
      });

      const data = await response.json();

      if (response.ok && data.users) {
        // Sort users alphabetically by account_name
        const sortedUsers = data.users.sort((a, b) =>
          a.account_name.localeCompare(b.account_name)
        );
        setUsers(sortedUsers);
      } else {
        setError(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDashboard = (user) => {
    // Set the viewing context and navigate to main dashboard
    setViewingAs({
      accountName: user.account_name,
      displayName: user.display_name || user.account_name
    });
    navigate('/');
  };

  const handleEditConfig = (user) => {
    // Navigate to config editor for this user
    navigate(`/admin/config/${encodeURIComponent(user.account_name)}`);
  };

  // Group users by first letter
  const groupedUsers = users.reduce((groups, user) => {
    const firstLetter = user.account_name.charAt(0).toUpperCase();
    if (!groups[firstLetter]) {
      groups[firstLetter] = [];
    }
    groups[firstLetter].push(user);
    return groups;
  }, {});

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Admin Panel</h1>
        <p>Manage user accounts and configurations</p>
      </div>

      {loading && (
        <div className="admin-loading">
          <p>Loading users...</p>
        </div>
      )}

      {error && (
        <div className="admin-error">
          <p>{error}</p>
          <button onClick={fetchUsers}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="admin-user-list">
          {Object.entries(groupedUsers).map(([letter, letterUsers]) => (
            <div key={letter} className="admin-user-group">
              <div className="admin-group-header">{letter}</div>
              {letterUsers.map((user) => (
                <div key={user.account_name} className="admin-user-card">
                  <div className="admin-user-info">
                    <div className="admin-user-name">
                      <MdPerson className="admin-icon" />
                      <span className="admin-account-name">{user.account_name}</span>
                      {user.display_name && (
                        <span className="admin-display-name">({user.display_name})</span>
                      )}
                    </div>
                    <div className="admin-user-badges">
                      {user.enabled ? (
                        <span className="admin-badge admin-badge-enabled">
                          <MdCheckCircle /> Enabled
                        </span>
                      ) : (
                        <span className="admin-badge admin-badge-disabled">
                          <MdCancel /> Disabled
                        </span>
                      )}
                      {user.beta && (
                        <span className="admin-badge admin-badge-beta">
                          <MdScience /> Beta
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="admin-user-actions">
                    <button
                      className="admin-action-btn admin-action-dashboard"
                      onClick={() => handleViewDashboard(user)}
                      title="View dashboard"
                    >
                      <MdPerson /> Dashboard
                    </button>
                    <button
                      className="admin-action-btn admin-action-config"
                      onClick={() => handleEditConfig(user)}
                      title="Edit configuration"
                    >
                      <MdSettings /> Config
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="admin-empty">
          <p>No users found</p>
        </div>
      )}
    </div>
  );
}

export default Admin;
